/**
 * Language certificate contract — shared by the brain's CEFR analyzer and the
 * upload surfaces that feed it, so the two cannot drift.
 *
 * @module heroes/checkup
 */

/**
 * The languages a CheckUp language test can certify. A certificate is ALWAYS
 * analysed for one of these, explicitly: an absent language is refused, never
 * read as English (H16 — a Portuguese certificate used to land in the English
 * pillar when the language was missing).
 */
export const LANGUAGE_TEST_LANGUAGES = ['english', 'portuguese'] as const;

export type LanguageTestLanguage = (typeof LANGUAGE_TEST_LANGUAGES)[number];

/** ISO 639-1 code the analyzer compares the certificate's assessed language against. */
export const LANGUAGE_TEST_LANGUAGE_ISO: Readonly<Record<LanguageTestLanguage, string>> = {
  english: 'en',
  portuguese: 'pt',
};

export function isLanguageTestLanguage(value: unknown): value is LanguageTestLanguage {
  return (
    typeof value === 'string' && (LANGUAGE_TEST_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * The document formats the analyzer can read (H14, F25 · D-14):
 *   - PNG / JPEG / GIF / WEBP → the vision model, as an image;
 *   - HEIC / HEIF (an iPhone camera roll) → converted to an image first;
 *   - a PDF or DOCX with text → the text is extracted and read as text;
 *   - a scanned PDF → its pages are rendered to images;
 *   - a DOCX whose certificate is an embedded picture → the picture is lifted
 *     out of the package and read as an image.
 */
export const LANGUAGE_CERTIFICATE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

/** Extensions for the same formats — some browsers match `accept` on these only. */
export const LANGUAGE_CERTIFICATE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.heic',
  '.heif',
  '.pdf',
  '.docx',
] as const;

/**
 * Most pages (PDF) or pictures (DOCX) the analyzer will read from one
 * certificate (D-2). Checked BEFORE anything is rendered or extracted — a
 * certificate is one or two pages, and each page is a vision-model image.
 */
export const LANGUAGE_CERTIFICATE_MAX_PAGES = 5;

/**
 * The most a DOCX package may expand to, in bytes, and the largest
 * uncompressed ÷ compressed ratio it may claim (D-2). A DOCX is a zip: without
 * these a few kilobytes of upload can inflate to gigabytes in the brain's
 * memory (a zip bomb). Checked on the package's own directory before any entry
 * is inflated, and enforced again on the real output while inflating, so a
 * directory that lies about its sizes is caught too.
 */
export const LANGUAGE_CERTIFICATE_MAX_ARCHIVE_BYTES = 25 * 1024 * 1024;
export const LANGUAGE_CERTIFICATE_MAX_COMPRESSION_RATIO = 100;

/**
 * Value for an `<input type="file" accept>` on a certificate upload. It used to
 * render the test's strategy ids (`english_speaking,speechaceSpeaking`) — not
 * MIME types — so the browser filtered nothing.
 */
export const LANGUAGE_CERTIFICATE_ACCEPT = [
  ...LANGUAGE_CERTIFICATE_MIME_TYPES,
  ...LANGUAGE_CERTIFICATE_EXTENSIONS,
].join(',');

/**
 * Why a language certificate produced no CEFR level — decided by the brain's
 * analyzer, where the cause is known, and returned to the caller as data.
 *
 * There is no "language not declared" reason: the language is REQUIRED at
 * every boundary (platform-api's schema, the brain DTO, the client types), so
 * an absent one is a 400 before any analyzer could be asked to guess (H16).
 */
export const LANGUAGE_TEST_FAILURE_REASONS = [
  /** The certificate attests a different language than the one declared (H16). */
  'language_mismatch',
  /** Neither a storage key nor bytes were supplied. */
  'no_input',
  /** The file could not be fetched from storage. */
  'storage_fetch_failed',
  /** Not a PNG, JPEG, GIF, WEBP, HEIC, PDF or DOCX (by its bytes, not its name). */
  'unsupported_document_type',
  /** A PDF, DOCX or HEIC that its parser could not open. */
  'document_unreadable',
  /** More pages (PDF) or pictures (DOCX) than LANGUAGE_CERTIFICATE_MAX_PAGES. */
  'document_too_many_pages',
  /** A DOCX package over the expansion or compression-ratio cap — a zip bomb, or near one. */
  'document_archive_too_large',
  /** A DOCX with neither text nor an embedded picture the analyzer can read. */
  'docx_without_text',
  /** The model read the document but found no CEFR level in it. */
  'no_cefr_found',
  /** The model's answer was not valid JSON, after the gateway's retries. */
  'invalid_model_response',
  /** The provider refused the request (a non-retriable 4xx). */
  'provider_rejected',
  /** The provider could not be reached or kept failing (5xx / 429 / timeout, retries exhausted). */
  'provider_unavailable',
  /**
   * platform-api got no answer from the brain (down, timed out, 5xx), so no
   * analyzer ran. Decided by the caller: it is the only party that knows.
   */
  'analyzer_unreachable',
  /** An error the analyzer does not recognise — a defect, reported to Sentry. */
  'internal_error',
] as const;

export type LanguageTestFailureReason = (typeof LANGUAGE_TEST_FAILURE_REASONS)[number];

export function isLanguageTestFailureReason(value: unknown): value is LanguageTestFailureReason {
  return (
    typeof value === 'string' &&
    (LANGUAGE_TEST_FAILURE_REASONS as readonly string[]).includes(value)
  );
}
